'use client';
import Head from 'next/head';
import { useState } from 'react';
import { Cylinder } from 'lucide-react';
import MethodPageLayout from '@/components/layouts/MethodPageLayout';
import CylinderVisualiser from '@/components/methods/CylinderVisualiser';
import SliderControl from '@/components/common/SliderControl';
import ColorSwatchRow from '@/components/common/ColorSwatchRow';
import SegmentedControl from '@/components/common/SegmentedControl';

const CYLINDER_COLORS = ['#185FA5', '#0F6E56', '#534AB7', '#854F0B', '#993C1D', '#f4a261'];
const UNIT_OPTIONS = ['mm', 'cm', 'm', 'km', 'in', 'ft'];

const DEFAULT_RADIUS = 5;
const DEFAULT_HEIGHT = 10;
const DEFAULT_COLOR = '#185FA5';
const DEFAULT_UNIT = 'cm';

export default function CylinderVolumePage() {
    const [radius, setRadius] = useState(DEFAULT_RADIUS);
    const [height, setHeight] = useState(DEFAULT_HEIGHT);
    const [fillColor, setFillColor] = useState(DEFAULT_COLOR);
    const [unit, setUnit] = useState(DEFAULT_UNIT);

    const onReset = () => {
        setRadius(DEFAULT_RADIUS);
        setHeight(DEFAULT_HEIGHT);
        setFillColor(DEFAULT_COLOR);
        setUnit(DEFAULT_UNIT);
    };

    const controls = (
        <>
            <SliderControl
                label="Radius (r)"
                min={0.1}
                max={15}
                step={0.1}
                value={radius}
                unit={unit}
                onChange={setRadius}
            />

            <SliderControl
                label="Height (h)"
                min={0.1}
                max={20}
                step={0.1}
                value={height}
                unit={unit}
                onChange={setHeight}
            />

            <SegmentedControl label="Unit" options={UNIT_OPTIONS} value={unit} onChange={setUnit} />

            <ColorSwatchRow
                label="Colour"
                colors={CYLINDER_COLORS}
                value={fillColor}
                onChange={setFillColor}
            />

            <div className="flex flex-col gap-1.5 border-t-[0.5px] border-line pt-3.5">
                <p className="text-[11px] font-medium uppercase tracking-[0.05em] text-hint">
                    About
                </p>
                <p className="text-[13px] leading-relaxed text-muted">
                    The volume of a cylinder is calculated by multiplying the area of the circular
                    base (π × r²) by the height (h).
                </p>
            </div>
        </>
    );

    return (
        <>
            <Head>
                <title>Cylinder Volume</title>
            </Head>
            <MethodPageLayout
                icon={<Cylinder size={20} />}
                iconColor="blue"
                title="Cylinder volume"
                description="Visualise and calculate the volume of a cylinder."
                category="visualiser"
                controls={controls}
                onReset={onReset}
            >
                <CylinderVisualiser
                    radius={radius}
                    height={height}
                    fillColor={fillColor}
                    unit={unit}
                />
            </MethodPageLayout>
        </>
    );
}
